import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useWizardStore } from '@/store/webinar';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export function WebinarStartStep() {
  useLanguage();
  const { data, updateData } = useWizardStore();

  // Auto-generate slug from name
  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (value: string) => {
    updateData('name', value);
    if (!data.slug || data.slug === generateSlug(data.name)) {
      updateData('slug', generateSlug(value));
    }
  };

  return (
    <div className="p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">{translateInterfaceText("Informações Básicas")}</h2>
        <p className="text-muted-foreground">
          {translateInterfaceText("Configure as informações principais do seu webinário")}</p>
      </div>

      <div className="space-y-6">
        {/* Nome do Webinário */}
        <div className="space-y-2">
          <Label htmlFor="name">{translateInterfaceText("Nome do Webinário *")}</Label>
          <Input
            id="name"
            placeholder={translateInterfaceText("Ex: Webinário de Lançamento Produto X")}
            value={data.name}
            onChange={(e) => handleNameChange(e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            {translateInterfaceText("Nome interno para identificação (não será exibido publicamente)")}</p>
        </div>

        {/* Título Público */}
        <div className="space-y-2">
          <Label htmlFor="title">{translateInterfaceText("Título Público *")}</Label>
          <Textarea
            id="title"
            placeholder={translateInterfaceText("Ex: Como Ganhar R$ 10.000 por Mês com Marketing Digital")}
            value={data.title}
            onChange={(e) => updateData('title', e.target.value)}
            rows={3}
          />
          <p className="text-sm text-muted-foreground">
            {translateInterfaceText("Título que será exibido para os participantes")}</p>
        </div>

        {/* URL Amigável */}
        <div className="space-y-2">
          <Label htmlFor="slug">{translateInterfaceText("URL Amigável *")}</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">/w/</span>
            <Input
              id="slug"
              placeholder="webinario-produto-x"
              value={data.slug}
              onChange={(e) => updateData('slug', generateSlug(e.target.value))}
              className="flex-1"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {translateInterfaceText("Link público do webinário:")}{" "}{window.location.origin}/w/{data.slug || translateInterfaceText("seu-link")}
          </p>
        </div>

        {/* Idioma */}
        <div className="space-y-2">
          <Label htmlFor="language">{translateInterfaceText("Idioma")}</Label>
          <Select value={data.language} onValueChange={(value) => updateData('language', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pt-BR">{translateInterfaceText("Português (Brasil)")}</SelectItem>
              <SelectItem value="en">{translateInterfaceText("English")}</SelectItem>
              <SelectItem value="es">{translateInterfaceText("Español")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Acesso Facilitado */}
        <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
          <div className="space-y-0.5 flex-1">
            <div className="flex items-center gap-2">
              <Label htmlFor="easy-access">{translateInterfaceText("Acesso Facilitado")}</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{translateInterfaceText("Permite entrada direta na sala sem formulário de login. Ideal para tráfego qualificado.")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("Participantes entram direto na sala sem preencher formulário")}</p>
          </div>
          <Switch
            id="easy-access"
            checked={data.easy_access}
            onCheckedChange={(checked) => updateData('easy_access', checked)}
          />
        </div>

        {/* Sincronizar com Horário */}
        <div className="flex items-center justify-between p-4 rounded-lg border bg-muted/50">
          <div className="space-y-0.5 flex-1">
            <div className="flex items-center gap-2">
              <Label htmlFor="sync-time">{translateInterfaceText("Sincronizar Vídeo com Horário")}</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>{translateInterfaceText("Exemplo: Se o webinário começa às 20h e alguém entra às 20:15, o vídeo inicia automaticamente no minuto 15.")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <p className="text-sm text-muted-foreground">
              {translateInterfaceText("O vídeo inicia no momento correspondente ao horário atual")}</p>
          </div>
          <Switch
            id="sync-time"
            checked={data.sync_with_time}
            onCheckedChange={(checked) => updateData('sync_with_time', checked)}
          />
        </div>
      </div>
    </div>
  );
}
